import actionTypes from 'constants/actionTypes'
import { createId } from 'lib/id'
import * as selectEdges from 'selectors/graph/edges'
import * as selectNodes from 'selectors/graph/nodes'
import direction from 'constants/direction'
import { getEdgeText } from 'lib/edge'
import selectPermissions from 'selectors/permissions'
import { selectMe } from 'selectors/auth'
import type { RootState } from 'types/state'
import type { GraphEdgeMap, GraphNodeMap } from 'types/domain'
import type { ActionShape, DispatchFn, GetStateFn } from 'types/adapters'

type Dispatch = DispatchFn<ActionShape>
type GetState = GetStateFn

const selectAllNodesWithDeleted = selectNodes.all as unknown as (
  state: RootState,
  props: { params: { deleted: boolean } }
) => GraphNodeMap

const selectAllEdgesWithDeleted = selectEdges.all as unknown as (
  state: RootState,
  props: { params: { deleted: boolean } }
) => GraphEdgeMap


export const selectOneEdge = ({ id }: { id: string }) => {
  return {
    type: actionTypes.EDGES.ONE.SELECT,
    payload: { id }
  }
}

export const deselectOneEdge = ({ id }: { id: string }) => {
  return {
    type: actionTypes.EDGES.ONE.DESELECT,
    payload: { id }
  }
}

export const createOneEdge = (
  {
    content,
    kind,
    id,
    from,
    to,
    directionality
  }: {
    content?: string
    kind?: string
    id?: string
    from?: string
    to?: string
    directionality?: string
  },
  notify = false
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (!(from && to)) {
      return
    }

    const state = getState() as RootState
    const { readers, writers } = selectPermissions(state)
    const { me } = selectMe(state)

    return dispatch({
      type: actionTypes.EDGES.ONE.CREATE,
      payload: {
        id: id || createId(),
        content,
        kind,
        from,
        to,
        creator: me?.id,
        directionality,
        readers: Object.keys(readers),
        writers: Object.keys(writers)
      },
      meta: {
        retry: true,
        request: 'edges.create',
        notify
      }
    })
  }
}

export const editOneEdge = (
  { id, content, kind, directionality }: { id: string; content?: string; kind?: string; directionality?: string },
  notify = false
) => {
  return {
    type: actionTypes.EDGES.ONE.EDIT,
    payload: {
      id,
      content,
      kind,
      directionality
    },
    meta: {
      retry: true,
      request: 'edges.edit',
      notify
    }
  }
}

export const deleteOneEdge = ({ id, autoConfirm = false }: { id: string; autoConfirm?: boolean }, notify = false) => {
  return {
    type: actionTypes.EDGES.ONE.DELETE,
    payload: { id },
    meta: {
      retry: true,
      request: 'edges.delete',
      notify,
      confirm: (state: RootState) => {
        if (notify || autoConfirm) {
          return null
        }
        const ns = selectAllNodesWithDeleted(state, { params: { deleted: true } })
        const es = selectAllEdgesWithDeleted(state, { params: { deleted: true } })
        const edge = es[id]
        if (!edge) {
          return null
        }
        const toId = edge.data?.to
        const fromId = edge.data?.from
        const to = toId ? ns[toId] : undefined
        const frm = fromId ? ns[fromId] : undefined
        const edgeContent = getEdgeText(edge, to, frm)
        return `Are you sure you want to delete this edge: ${edgeContent}`
      }
    }
  }
}

export const toggleDirectionality = ({ id }: { id: string }) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const es = selectEdges.all(getState() as RootState)
    const e = es[id]
    if (!e) {
      return
    }

    const current = e.data?.directionality
    const nextDirectionality =
      current === direction.DIRECTED ? direction.UNDIRECTED : // directed -> undirected
      current === direction.UNDIRECTED ? direction.BIDIRECTIONAL : // undirected -> bidirectional
      current === direction.BIDIRECTIONAL ? direction.REVERSE : // bidirectional -> reverse
      direction.UNDIRECTED // default undirected

    return dispatch(setDirectionality({ id, directionality: nextDirectionality }))
  }
}

export const setDirectionality = ({ id, directionality }: { id: string; directionality: string }, notify = false) => {
  return {
    type: actionTypes.EDGES.ONE.EDIT,
    payload: {
      id,
      directionality
    },
    meta: {
      retry: true,
      request: 'edges.edit',
      notify
    }
  }
}

export const selectMultiEdge = ({ ids }: { ids: string[] }) => {
  return {
    type: actionTypes.EDGES.MULTI.SELECT,
    payload: { ids }
  }
}

export const deleteMultiEdge = ({ ids }: { ids: string[] }, notify = false) => {
  return (dispatch: Dispatch) => {
    if (ids.length === 0) {
      return
    }

    return dispatch({
      type: actionTypes.EDGES.MULTI.DELETE,
      payload: { ids },
      meta: {
        retry: true,
        request: 'edges.deleteMulti',
        notify,
        confirm: (state: RootState) => {
          if (notify) {
            return null
          }
          const ns = selectAllNodesWithDeleted(state, { params: { deleted: true } })
          const es = selectAllEdgesWithDeleted(state, { params: { deleted: true } })
          const edgesContent = ids
            .map((id) => {
              const edge = es[id]
              if (!edge) {
                return ''
              }
              const toId = edge.data?.to
              const fromId = edge.data?.from
              const to = toId ? ns[toId] : undefined
              const frm = fromId ? ns[fromId] : undefined
              return getEdgeText(edge, to, frm)
            })
            .filter(Boolean)
            .join(', ')
          return `Are you sure you want to delete these edges: ${edgesContent}`
        }
      }
    })
  }
}
